import type { SubmissionStatus, SubmissionStatusView, SubmissionType } from '@innspel/core';
import { lofteUtenFrist, submissionStatus, type Tokennavn } from '../generated/tokens.ts';
import { saksnummerAv } from './saksnummer.ts';
import { lofte, tekst } from './tekster.ts';

/**
 * The status as the submitter sees it. EVERYTHING comes from `submissionStatus` in
 * `theme/theme.ts`, through the generated map: the name per track (DD-22),
 * the sentence the submitter gets (DD-08), the icon name and the token names for colour.
 *
 * Before 2026-09-14 the name, colour and promise were hard-coded here AND in
 * `@innspel/react-native` AND in the panel, and they diverged: «Vurderes» was yellow here and
 * blue in the panel, «Planlagt» green here and brand-coloured there, and `kjent_feil` was called
 * «Kjent, ikke planlagt» here and «Kjent feil» in the theme (UX review C5).
 *
 * DD-08 / «Styling» in CLAUDE.md: **colour never carries meaning on its own.** Every status
 * has an icon AND a text label. `brand ↔ info` and `brand ↔ danger` are measured at under
 * 1.5 in contrast against each other by the theme generator, so the label is what
 * actually carries the meaning.
 */
export interface StatusVisning {
  /** Ionicons name from the theme — never an emoji (brand-ui-guidelines). */
  ikon: string;
  etikett: string;
  /** Token name in the active palette, not a colour. */
  fg: Tokennavn;
  bg: Tokennavn;
  lofte: string;
}

/** Norwegian date without time of day — the deadline is a date, not a point in time (DD-28). */
export function formaterFrist(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'long' }).format(d);
}

/**
 * Date AND time of day — «8. desember 14:02». Only for a point in time the submitter
 * compares with now: when the list she sees offline was last fetched (H3.1, concept screen 3).
 * A deadline is never shown this way (DD-28).
 */
export function formaterTidspunkt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(d);
}

export function statusVisning(status: SubmissionStatus, type: SubmissionType, deadlineAt?: string): StatusVisning {
  const o = submissionStatus[status];
  const spor: 'feil' | 'onske' = type === 'onske' ? 'onske' : 'feil';
  return {
    ikon: o.ikon,
    etikett: o.etikett[spor],
    fg: o.fg,
    bg: o.bg,
    lofte: settInnFrist(o.lofte[spor], deadlineAt),
  };
}

/**
 * `{frist}` is replaced with the date (DD-28). Without a deadline there is no time promise, and
 * the deadline-free sentence stands instead — never an empty placeholder on screen.
 */
function settInnFrist(mal: string, deadlineAt: string | undefined): string {
  if (!mal.includes('{frist}')) return mal;
  return deadlineAt ? mal.replace('{frist}', formaterFrist(deadlineAt)) : lofteUtenFrist;
}

/** The row waiting for the network (DD-41). It has no case number and no promise of a reply. */
export const venterVisning: StatusVisning = {
  ikon: 'cloud-offline-outline',
  etikett: 'Venter på nett',
  fg: 'textSecondary',
  bg: 'neutralBg',
  lofte: lofte.venterNett,
};

/** Label for a whole row, as the screen reader's summary. The icon is `aria-hidden`, so the label carries everything. */
export function radEtikett(rad: SubmissionStatusView): string {
  const v = statusVisning(rad.status, rad.type, rad.deadlineAt);
  const spor = rad.type === 'onske' ? tekst.onske : tekst.feil;
  return `${tekst.sakHode(spor, saksnummerAv(rad))}: ${rad.text}. Status ${v.etikett.toLowerCase()}. ${v.lofte}${rad.attachmentRemoved ? ` ${tekst.bildeFjernet}` : ''}`;
}
